import { useState } from 'react';
import { Icon } from './Icons.jsx';
import { MobHeader, FooterBar } from './Stepper.jsx';
import { createPayment } from '../api/payments.js';
import { createOrder, createOrderGroup } from '../api/orders.js';
import { maskExpiry } from '../utils/format.js';

// "MM/AA" -> { expiry_month, expiry_year } (ano de 2 dígitos -> 20XX).
// A Asaas não tem SDK de tokenização client-side para formulário próprio
// (checkout transparente) — o dado precisa ir pro backend, que repassa pro
// gateway sem persistir (só card_last_four/card_brand, vindos da resposta,
// vão para o banco).
function parseExpiry(exp) {
  const [mm, yy] = (exp || '').split('/').map(s => s.trim());
  const month = parseInt(mm, 10);
  let year = parseInt(yy, 10);
  if (yy?.length === 2) year += 2000;
  return { expiry_month: month, expiry_year: year };
}

export default function Step5Payment({ state, setState, onNext, onBack, profile, onEditAddress }) {
  const [method, setMethod] = useState(state.payment?.method || 'pix');
  // A Asaas exige CEP + endereço do titular pra cobrança por cartão (ver
  // `_billing_address` no backend) — sem isso o pagamento falha só depois
  // de "Pagar" com um 502 confuso. Checa antes e guia pro perfil.
  const addressComplete = !!(profile?.cep && profile?.address && profile?.city && profile?.state);
  const [card, setCard] = useState({ number: '', name: '', exp: '', cvv: '' });
  const [installments, setInstallments] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pixData, setPixData] = useState(null);

  const lab = state.labData;
  // Compra combinada: cada exame vai para o laboratório da combinação que o
  // oferece; exames sem oferta na região ficam fora da compra.
  const combo = !lab ? state.combo : null;
  const comboPrice = {};
  const comboLabOf = {};
  (combo?.parts || []).forEach(p => p.codes.forEach(c => { comboPrice[c] = p.prices[c]; comboLabOf[c] = p; }));
  const exams = (state.exams || []).filter(e => !combo || comboPrice[e.code] != null);
  const priceOf = (code) => (lab ? lab.prices?.[code] : comboPrice[code]) || 0;
  const subtotal = exams.reduce((s, e) => s + priceOf(e.code), 0);
  const discount = method === 'pix' ? subtotal * 0.05 : 0;
  const total = subtotal - discount;
  const fmt = n => n.toFixed(2).replace('.', ',');

  const pay = async () => {
    setError(null);
    setLoading(true);
    try {
      // Cria o pedido real na primeira tentativa de pagamento
      let orderId = state.orderId;
      if (!orderId) {
        const uploads = state.uploads || [];
        // Um Prescription por pedido médico; cada exame vai para o pedido de onde veio (sources[0])
        const prescriptions = uploads.map(u => ({
          doctor_name: u.doctor?.name || null,
          doctor_crm: u.doctor?.crm || null,
          doctor_specialty: u.doctor?.specialty || null,
          document_date: u.doctor?.date || null,
          file_url: u.fileUrl || null,
          ocr_json: u.ocr || null,
          tuss_codes: exams.filter(e => e.sources?.[0] === u.id).map(e => e.code),
        })).filter(p => !combo || p.tuss_codes.length > 0);
        const manual_tuss_codes = exams.filter(e => !(e.sources?.length)).map(e => e.code);
        const common = {
          prescriptions,
          manual_tuss_codes,
          search_cep: state.location?.cep || null,
          search_radius_km: state.location?.radius || 5,
        };
        if (combo && combo.parts.length > 1) {
          const orders = await createOrderGroup({
            ...common,
            parts: combo.parts.map(p => ({ partner_id: p.id, tuss_codes: p.codes })),
          });
          orderId = orders[0].id;
          setState(s => ({ ...s, orderId, order: orders[0], orders }));
        } else {
          const partnerId = lab ? lab.id : combo.parts[0].id;
          const order = await createOrder({ ...common, partner_id: partnerId });
          orderId = order.id;
          setState(s => ({ ...s, orderId, order, orders: [order] }));
        }
      }

      const paymentData = await createPayment({
        order_id: orderId,
        method: method === 'card' ? 'credit_card' : method,
        installments,
        card: method === 'card'
          ? {
              number: card.number.replace(/\s/g, ''),
              holder_name: card.name,
              cvv: card.cvv,
              ...parseExpiry(card.exp),
            }
          : undefined,
      });
      // Dados do cartão nunca vão para o estado global — somente o resultado
      setState(s => ({ ...s, payment: { method, installments, total, ...paymentData } }));

      if (method === 'pix' && paymentData.pix_qr_code) {
        // Mostra o QR real e aguarda o usuário prosseguir
        setPixData({ qr: paymentData.pix_qr_code, copyPaste: paymentData.pix_copy_paste });
      } else {
        onNext();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Não foi possível processar o pagamento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <MobHeader title="Pagamento" onBack={onBack}/>
      <div style={{ flex: 1, minHeight: 0, padding: '18px 16px', overflowY: 'auto', background: 'var(--bg)' }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>Como deseja pagar?</h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
          {[
            { k: 'pix', l: 'PIX', sub: '5% de desconto · aprovação instantânea', icon: 'pix' },
            { k: 'card', l: 'Cartão de crédito', sub: 'em até 6x sem juros', icon: 'card' },
          ].map(m => {
            const Ic = Icon[m.icon]; const sel = method === m.k;
            return (
              <button key={m.k} onClick={() => setMethod(m.k)} style={{ padding: 14, borderRadius: 12, textAlign: 'left', background: 'var(--white)', width: '100%', border: '2px solid ' + (sel ? 'var(--brand-primary)' : 'var(--border)'), display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: sel ? 'var(--brand-primary-soft)' : 'var(--marble-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Ic size={20} color={sel ? 'var(--brand-primary)' : 'var(--midnight)'}/>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14 }}>{m.l}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{m.sub}</div>
                </div>
                <span style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid ' + (sel ? 'var(--brand-primary)' : 'var(--border-strong)'), background: sel ? 'var(--brand-primary)' : 'var(--white)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {sel && <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--white)' }}/>}
                </span>
              </button>
            );
          })}
        </div>

        <div className="card" style={{ padding: 16, marginTop: 14 }}>
          {method === 'pix' && (
            <div style={{ textAlign: 'center' }}>
              {pixData ? (
                <>
                  <div style={{ width: 140, height: 140, margin: '0 auto', padding: 10, background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 12 }}>
                    <img src={`data:image/png;base64,${pixData.qr}`} alt="QR Code PIX" style={{ width: 120, height: 120 }}/>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 10 }}>Escaneie no app do seu banco</div>
                  {pixData.copyPaste && (
                    <button
                      className="btn btn-outline"
                      style={{ marginTop: 10, minHeight: 42, padding: '10px 16px' }}
                      onClick={() => navigator.clipboard?.writeText(pixData.copyPaste)}
                    >
                      Copiar código PIX
                    </button>
                  )}
                </>
              ) : (
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', textAlign: 'left' }}>
                  <Icon.info size={18} color="var(--text-muted)" style={{ flexShrink: 0, marginTop: 1 }}/>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    O QR Code será gerado quando você confirmar o pagamento. Aprovação em segundos após o PIX.
                  </div>
                </div>
              )}
            </div>
          )}
          {method === 'card' && !addressComplete && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: 12, background: 'var(--warning-tint)', borderRadius: 10 }}>
              <Icon.info size={18} color="var(--amber-dark)" style={{ flexShrink: 0, marginTop: 1 }}/>
              <div style={{ fontSize: 13, color: 'var(--amber-dark)' }}>
                Para pagar com cartão, complete seu endereço no perfil (a operadora exige o CEP do titular).
                <button
                  type="button"
                  onClick={onEditAddress}
                  style={{ display: 'block', marginTop: 6, fontWeight: 700, color: 'var(--brand-primary)' }}
                >
                  Completar endereço
                </button>
              </div>
            </div>
          )}
          {method === 'card' && addressComplete && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <label className="input-label">Número do cartão</label>
                <input className="input" value={card.number} onChange={e => setCard({ ...card, number: e.target.value })} placeholder="0000 0000 0000 0000" inputMode="numeric" autoComplete="cc-number" name="cc-number"/>
              </div>
              <div>
                <label className="input-label">Nome no cartão</label>
                <input className="input" value={card.name} onChange={e => setCard({ ...card, name: e.target.value })} placeholder="NOME SOBRENOME" autoComplete="cc-name" name="cc-name"/>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <label className="input-label">Validade</label>
                  <input className="input" value={card.exp} onChange={e => setCard({ ...card, exp: maskExpiry(e.target.value) })} placeholder="MM/AA" inputMode="numeric" autoComplete="cc-exp" name="cc-exp"/>
                </div>
                <div>
                  <label className="input-label">CVV</label>
                  <input className="input" value={card.cvv} onChange={e => setCard({ ...card, cvv: e.target.value })} placeholder="123" type="password" inputMode="numeric" autoComplete="cc-csc" name="cc-csc"/>
                </div>
              </div>
              <div>
                <label className="input-label">Parcelas</label>
                <select className="input" value={installments} onChange={e => setInstallments(+e.target.value)}>
                  {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}x de R$ {fmt(total/n)}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="card" style={{ padding: 14, marginTop: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'var(--font-display)', marginBottom: 10 }}>Resumo</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
            {combo && combo.parts.length > 1 ? combo.parts.map(p => (
              <div key={p.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 12.5, marginTop: 4 }}>{p.name}</div>
                {exams.filter(e => comboLabOf[e.code]?.id === p.id).map(e => (
                  <div key={e.code} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, paddingLeft: 8 }}>
                    <span style={{ color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</span>
                    <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, flexShrink: 0 }}>R$ {fmt(priceOf(e.code))}</span>
                  </div>
                ))}
              </div>
            )) : exams.map(e => (
              <div key={e.code} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</span>
                <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, flexShrink: 0 }}>R$ {fmt(priceOf(e.code))}</span>
              </div>
            ))}
            {combo && (combo.missing || []).length > 0 && (
              <div style={{ fontSize: 11.5, color: 'var(--warning-darker)' }}>Fora desta compra (sem oferta na região): {combo.missing.map(m => m.tuss_name).join(', ')}</div>
            )}
            <div style={{ height: 1, background: 'var(--border)', margin: '6px 0' }}/>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-muted)' }}><span>Subtotal</span><span>R$ {fmt(subtotal)}</span></div>
            {discount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--green-dark)' }}><span>Desconto PIX</span><span>− R$ {fmt(discount)}</span></div>}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600 }}>Total</span>
            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 22, color: 'var(--brand-primary)' }}>R$ {fmt(total)}</span>
          </div>
        </div>

        {error && (
          <div role="alert" style={{ marginTop: 14, padding: '12px 14px', background: 'var(--primary-tint)', borderRadius: 10, color: 'var(--brand-primary)', fontSize: 13, fontWeight: 600 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14, fontSize: 11, color: 'var(--text-muted)' }}>
          <Icon.shield size={14} color="var(--green-dark)"/>
          Pagamento seguro · dados criptografados · Asaas
        </div>
      </div>
      <FooterBar
        onBack={onBack}
        onNext={pixData ? onNext : pay}
        nextLabel={pixData ? 'Já paguei · Continuar' : `Pagar R$ ${fmt(total)}`}
        loading={loading}
        canNext={(!!lab || !!combo) && exams.length > 0 && (method !== 'card' || addressComplete)}
        hint={!lab && !combo ? 'Volte e selecione um laboratório' : (method === 'card' && !addressComplete) ? 'Complete seu endereço para pagar com cartão' : null}
      />
    </>
  );
}
